import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../interfaces/User';
import { Response } from '../../interfaces/Response';
import { IPaginatableService } from 'src/app/interfaces/IPaginTableService';
import { Debug } from 'src/app/utils/debug';

@Injectable({
  providedIn: 'root',
})
export class UserService implements IPaginatableService {
  private apiUrl = environment.apiUrl;
  private responseSubject = new BehaviorSubject<Response>({
    data: [],
    navigation: {
      page: 0,
      totalPages: 0,
      totalRows: 0,
      itemsPerPage: 0,
    },
    success: false,
    error: '',
    status: 0,
  });

  constructor(private http: HttpClient) {}
  getDataByPage(page: number, itemsPerPage: number): Observable<Response> {
    return this.http
      .get<Response>(`${this.apiUrl}/users?page=${page}&limit=${itemsPerPage}`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching groups:', error);
          return throwError(() => error);
        })
      );
  }

  // Fetch and emit the latest users along with pagination data
  getUsers(): Observable<Response> {
    this.http
      .get<Response>(`${this.apiUrl}/users`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching users:', error);
          return throwError(() => error);
        })
      )
      .subscribe((response: Response) => {
        this.responseSubject.next(response);
      });

    return this.responseSubject.asObservable();
  }

  createUser(user: User): Observable<Response> {
    return this.http.post<Response>(`${this.apiUrl}/users`, user).pipe(
      catchError((error) => {
        console.error('Error creating user:', error);
        return throwError(() => error);
      }),
      tap((response: Response) => {
        if (response && response.data) {
          const currentUsers = this.responseSubject.getValue().data;
          currentUsers.push(response.data);
          this.responseSubject.next({
            ...this.responseSubject.getValue(),
            data: currentUsers,
          });
        }
      })
    );
  }

  getUserById(id: number): Observable<Response> {
    return this.http.get<Response>(`${this.apiUrl}/users/${id}`).pipe(
      catchError((error) => {
        console.error('Error fetching user:', error);
        return throwError(() => error);
      })
    );
  }

  // Update (PUT)
  updateUser(id: string, user: User): Observable<Response> {
    Debug.log(`about to update user ${id}`, user);
    return this.http.patch<Response>(`${this.apiUrl}/users/${id}`, user).pipe(
      catchError((error) => {
        console.error('Error updating user:', error);
        return throwError(() => error);
      }),
      tap((response: Response) => {
        if (response && response.data) {
          const updatedUsers = this.responseSubject
            .getValue()
            .data.map((u) => (u.id === id ? response.data : u));
          this.responseSubject.next({
            ...this.responseSubject.getValue(),
            data: updatedUsers,
          });
        }
      })
    );
  }

  // Delete (DELETE)
  deleteUser(id: string): Observable<Response> {
    return this.http.delete<Response>(`${this.apiUrl}/users/${id}`).pipe(
      catchError((error) => {
        console.error('Error deleting user:', error);
        return throwError(() => error);
      }),
      tap(() => {
        const updatedUsers = this.responseSubject
          .getValue()
          .data.filter((u) => u.id !== id);
        this.responseSubject.next({
          ...this.responseSubject.getValue(),
          data: updatedUsers,
        });
      })
    );
  }

  getAllPermissions(): Observable<any> {
    return this.http.get<Response>(`${this.apiUrl}/permissions/`).pipe(
      catchError((error) => {
        console.error('Error fetching user:', error);
        return throwError(() => error);
      })
    );
  }
  getAllProfiles(): Observable<Response> {
    return this.http.get<Response>(`${this.apiUrl}/profiles/`).pipe(
      catchError((error) => {
        console.error('Error fetching profiles:', error);
        return throwError(() => error);
      })
    );
  }
  getAllUsers(): Observable<Response> {
    //first 2000 users this is to be replaced with direct match by patter as we type the user mail or name
    return this.http
      .get<Response>(`${this.apiUrl}/users?page=1&pageSize=2000`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching profiles:', error);
          return throwError(() => error);
        })
      );
  }

  newProfile(profile: string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/profiles`, { name: profile })
      .pipe(
        catchError((error) => {
          console.error('Error creating profile:', error);
          return throwError(() => error);
        })
      );
  }

  updateProfile(id: string, profile: any): Observable<any> {
    return this.http
      .patch<Response>(`${this.apiUrl}/profiles/${id}`, profile)
      .pipe(
        catchError((error) => {
          console.error('Error updating user:', error);
          return throwError(() => error);
        }),
        tap((response: Response) => {
          if (response && response.data) {
            const updatedUsers = this.responseSubject
              .getValue()
              .data.map((u) => (u.id === id ? response.data : u));
            this.responseSubject.next({
              ...this.responseSubject.getValue(),
              data: updatedUsers,
            });
          }
        })
      );
  }

  deleteProfile(id: string): Observable<Response> {
    return this.http.delete<Response>(`${this.apiUrl}/profiles/${id}`).pipe(
      catchError((error) => {
        console.error('Error deleting profile:', error);
        return throwError(() => error);
      }),
      tap(() => {
        const updatedUsers = this.responseSubject
          .getValue()
          .data.filter((u) => u.id !== id);
        this.responseSubject.next({
          ...this.responseSubject.getValue(),
          data: updatedUsers,
        });
      })
    );
  }

  saveProfilePermissions(permissions: any): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/profiles/update-permissions`, permissions)
      .pipe(
        catchError((error) => {
          console.error('Error saving  permissions:', error);
          return throwError(() => error);
        })
      );
  }

  saveAccreditations(accreditations: any): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/accreditations`, accreditations)
      .pipe(
        catchError((error) => {
          console.error('Error fetching users:', error);
          return throwError(() => error);
        })
      );
  }

  removeAccreditation(id: any): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/accreditations/${id}`).pipe(
      catchError((error) => {
        console.error('Error fetching users:', error);
        return throwError(() => error);
      })
    );
  }

  getAllAccreditations(): Observable<Response> {
    return this.http.get<Response>(`${this.apiUrl}/accreditations/`).pipe(
      catchError((error) => {
        console.error('Error fetching user:', error);
        return throwError(() => error);
      })
    );
  }

  changePassword(userId: number, newPassword: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/users/${userId}/password`, {
      newPassword,
    });
  }
}
